import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Erstellt ein optisch ansprechendes PDF mit Ersparnis-Vergleich
 * für eine Schulden-Gruppe (Kundenpreis vs. gewählter Preis).
 *
 * @param {Object} params
 * @param {string} params.kundenName - Name des Kunden (Empfänger)
 * @param {Array}  params.posten - Posten: { produkt_name, menge, betrag, kunde_preis, price_category, ... }
 * @param {string} [params.datum] - Datum als ISO-String (default: heute)
 * @param {string} [params.firmenName] - Firmenname (wird oben im Header angezeigt, wenn gesetzt)
 * @returns {jsPDF|null} - jsPDF instance oder null wenn keine Ersparnis
 */
export function generateErspanisPdf({ kundenName, posten, datum, firmenName }) {
  // Nur Posten mit price_category und kunde_preis (Rabatt möglich)
  const posten_mit_rabatt = (posten || []).filter(p =>
    p.price_category && p.price_category !== "kunde" &&
    typeof p.kunde_preis === "number" && p.kunde_preis > 0
  );
  if (posten_mit_rabatt.length === 0) return null;

  // Rechnungsnummer aus gruppe_name oder rechnung_nummer (falls vorhanden)
  const rechnungsNr = posten[0]?.gruppe_name || posten[0]?.rechnung_nummer || null;

  // Berechnungen
  const rows = posten_mit_rabatt.map(p => {
    const menge = parseInt(p.menge) || 1;
    const kundeTotal = (p.kunde_preis || 0) * menge;
    const gewaehlter = (parseFloat(p.betrag) || 0); // Gesamtbetrag des Postens (inkl. Zuschlag × Menge)
    return {
      name: p.produkt_name || p.beschreibung || "Posten",
      menge,
      kunde_einzel: p.kunde_preis || 0,
      kunde_total: kundeTotal,
      gewaehlter_einzel: menge > 0 ? gewaehlter / menge : gewaehlter,
      gewaehlter_total: gewaehlter,
      kategorie: labelFromCategory(p.price_category),
    };
  });

  const sumKunde = rows.reduce((s, r) => s + r.kunde_total, 0);
  const sumGewaehlt = rows.reduce((s, r) => s + r.gewaehlter_total, 0);
  const ersparnisAbs = sumKunde - sumGewaehlt;
  const ersparnisProz = sumKunde > 0 ? (ersparnisAbs / sumKunde) * 100 : 0;

  if (ersparnisAbs <= 0) return null;

  // ---- PDF erstellen ----
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  // Farben (BurningTeam grün/teal)
  const EMERALD = [16, 185, 129];
  const EMERALD_DARK = [5, 150, 105];
  const SLATE = [51, 65, 85];
  const SLATE_LIGHT = [148, 163, 184];
  const GOLD = [245, 158, 11];

  // -------- Header-Banner --------
  doc.setFillColor(...EMERALD);
  doc.rect(0, 0, pageW, 38, "F");

  const titleTop = (firmenName && firmenName.trim()) ? firmenName.trim() : null;

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  if (titleTop) {
    doc.setFontSize(24);
    doc.text(titleTop, 14, 18);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("Dein persönliches Ersparnis-Angebot", 14, 26);
  } else {
    // Kein Firmenname: Tagline zentraler/größer
    doc.setFontSize(18);
    doc.text("Dein persönliches Ersparnis-Angebot", 14, 22);
  }

  const dStr = new Date(datum || Date.now()).toLocaleDateString("de-DE", {
    day: "2-digit", month: "long", year: "numeric"
  });
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(dStr, pageW - 14, 18, { align: "right" });

  // -------- Kunden-Block --------
  doc.setTextColor(...SLATE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Für:", 14, 52);
  doc.setFontSize(18);
  doc.setTextColor(...EMERALD_DARK);
  doc.text(kundenName || "Kunde", 30, 52);

  if (rechnungsNr) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...SLATE_LIGHT);
    doc.text(`Rechnung: ${rechnungsNr}`, pageW - 14, 52, { align: "right" });
  }

  doc.setDrawColor(...SLATE_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(14, 58, pageW - 14, 58);

  // -------- Tabelle 1: Regulärer Preis --------
  doc.setTextColor(...SLATE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("1. Übersicht zum regulären Preis", 14, 68);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE_LIGHT);
  doc.text("(so würdest Du ohne Angebot zahlen)", 14, 73);

  autoTable(doc, {
    startY: 77,
    head: [["Produkt", "Menge", "Einzelpreis", "Gesamt"]],
    body: rows.map(r => [
      r.name,
      `${r.menge}x`,
      fmtEur(r.kunde_einzel),
      fmtEur(r.kunde_total),
    ]),
    foot: [[
      { content: "Gesamt regulärer Preis", colSpan: 3, styles: { halign: "right", fontStyle: "bold", textColor: SLATE, fillColor: [241, 245, 249] } },
      { content: fmtEur(sumKunde), styles: { halign: "right", fontStyle: "bold", textColor: SLATE, fillColor: [241, 245, 249] } },
    ]],
    theme: "grid",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: SLATE },
    headStyles: { fillColor: SLATE, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      1: { halign: "center", cellWidth: 20 },
      2: { halign: "right", cellWidth: 35 },
      3: { halign: "right", cellWidth: 35 },
    },
  });

  const afterT1 = doc.lastAutoTable.finalY + 12;

  // -------- Tabelle 2: Dein Preis --------
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...EMERALD_DARK);
  doc.setFontSize(12);
  doc.text("2. DEIN persönlicher Angebotspreis", 14, afterT1);

  autoTable(doc, {
    startY: afterT1 + 5,
    head: [["Produkt", "Menge", "Einzelpreis", "Gesamt"]],
    body: rows.map(r => [
      r.name,
      `${r.menge}x`,
      fmtEur(r.gewaehlter_einzel),
      fmtEur(r.gewaehlter_total),
    ]),
    foot: [[
      { content: "DEIN Gesamtpreis", colSpan: 3, styles: { halign: "right", fontStyle: "bold", textColor: [255, 255, 255], fillColor: EMERALD } },
      { content: fmtEur(sumGewaehlt), styles: { halign: "right", fontStyle: "bold", textColor: [255, 255, 255], fillColor: EMERALD } },
    ]],
    theme: "grid",
    styles: { font: "helvetica", fontSize: 10, cellPadding: 3, textColor: SLATE },
    headStyles: { fillColor: EMERALD_DARK, textColor: [255, 255, 255], fontStyle: "bold" },
    columnStyles: {
      1: { halign: "center", cellWidth: 20 },
      2: { halign: "right", cellWidth: 35 },
      3: { halign: "right", cellWidth: 35 },
    },
  });

  const afterT2 = doc.lastAutoTable.finalY + 10;

  // -------- Ersparnis-Highlight-Box --------
  const boxY = afterT2;
  const boxH = 38;
  const boxX = 14;
  const boxW = pageW - 28;

  doc.setFillColor(...EMERALD);
  doc.roundedRect(boxX, boxY, boxW, boxH, 4, 4, "F");

  // Stern-Symbol links
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.text("*", boxX + 10, boxY + 24);

  // Text mittig
  doc.setFontSize(13);
  doc.text("Deine Ersparnis", boxX + 24, boxY + 14);

  doc.setFontSize(26);
  doc.text(fmtEur(ersparnisAbs), boxX + 24, boxY + 28);

  // Prozent-Badge rechts
  doc.setFillColor(...GOLD);
  const badgeW = 50;
  const badgeX = boxX + boxW - badgeW - 8;
  const badgeY = boxY + (boxH - 20) / 2;
  doc.roundedRect(badgeX, badgeY, badgeW, 20, 3, 3, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text(`-${ersparnisProz.toFixed(1)}%`, badgeX + badgeW / 2, badgeY + 13, { align: "center" });

  // -------- Footer --------
  const footY = 285;
  doc.setDrawColor(...SLATE_LIGHT);
  doc.setLineWidth(0.2);
  doc.line(14, footY, pageW - 14, footY);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SLATE_LIGHT);
  doc.text(
    `Erstellt mit BurningTeam am ${new Date().toLocaleDateString("de-DE")}`,
    pageW / 2, footY + 5, { align: "center" }
  );

  return doc;
}

export function downloadErspanisPdf(params) {
  const doc = generateErspanisPdf(params);
  if (!doc) return false;
  const safeName = (params.kundenName || "kunde").replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, "_");
  const dStr = new Date(params.datum || Date.now()).toISOString().split("T")[0];
  doc.save(`BurningTeam_Ersparnis_${safeName}_${dStr}.pdf`);
  return true;
}

export function hasErspanis(posten) {
  const filtered = (posten || []).filter(p =>
    p.price_category && p.price_category !== "kunde" &&
    typeof p.kunde_preis === "number" && p.kunde_preis > 0
  );
  if (filtered.length === 0) return false;
  const sumKunde = filtered.reduce((s, p) => s + (p.kunde_preis || 0) * (parseInt(p.menge) || 1), 0);
  const sumGewaehlt = filtered.reduce((s, p) => s + (parseFloat(p.betrag) || 0), 0);
  return sumKunde - sumGewaehlt > 0;
}

// Helpers
function fmtEur(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function labelFromCategory(cat) {
  const map = {
    kunde: "Kunde",
    tp: "Teampartner",
    ba: "BA",
    five_plus_one: "5+1",
    ap: "AP",
  };
  return map[cat] || cat || "-";
}
